import { useEffect, useRef, useState } from "react";
import { addDocumentData, auth } from "../../firebase";
import {
  formatEventDateForDisplay,
  getMissingEventDetailKeys,
  isEventDateTimeInFuture,
  normalizeEventDetails,
} from "../../Utils/eventDetails";
import Waze from "../Waze";
import { isInvitationNavigationEnabled } from "../../Utils/invitationNavigation";
import EventDatePicker from "../EventDatePicker";
import EventTimePicker, { DEFAULT_EVENT_TIME } from "../EventTimePicker";
import FileDisplay from "../FileDisplay";
import { getInvitationEditorAvailability, openInvitationEditor } from "../../Utils/invitationEditingApi";
import { callInvitationWatermark } from "../../Utils/invitationWatermarkApi";
import { getSettingsThumbnailUrl } from "../../Utils/invitationThumbnail";
import "./EventDetailsModal.css";

const STEP_COUNT = 3;
// The full-size invitation screen before the questions. Kept, switched off: the
// invitation now shows small beside the first question, from the same preview.
const SHOW_INVITATION_INTRO_STEP = false;
// The edit button beside the first question. Kept, hidden for now.
const SHOW_EDIT_ON_FIRST_QUESTION = false;
// A phone keyboard takes at least this much of the visible height.
const KEYBOARD_MIN_HEIGHT = 120;
const TITLE_STARTS = ["החתונה של ", "בר המצווה של ", "בת המצווה של ", "יום ההולדת של ", "מסיבת הברית של ", "טקס הברית של ", "מסיבת הבריתה של "];
const WELCOME_ROBOT = "https://ucarecdn.com/50c649f8-64a8-480f-9136-985b0772920e/noroot.png";
const HEBREW_DAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
const HEBREW_MONTHS = ["בינואר", "בפברואר", "במרץ", "באפריל", "במאי", "ביוני", "ביולי", "באוגוסט", "בספטמבר", "באוקטובר", "בנובמבר", "בדצמבר"];

// Answers are kept on this device until saved, so leaving halfway does not lose them.
const draftKey = (docID) => `fb-event-details-draft:${docID}`;
const readDraft = (docID) => {
  try {
    const draft = JSON.parse(window.localStorage.getItem(draftKey(docID)) || "null");
    return draft && typeof draft === "object" ? draft : null;
  } catch (_error) {
    return null;
  }
};
const writeDraft = (docID, details) => {
  try {
    window.localStorage.setItem(draftKey(docID), JSON.stringify(details));
  } catch (_error) {
    // Storage can be unavailable (private mode); the form still works.
  }
};
const clearDraft = (docID) => {
  try {
    window.localStorage.removeItem(draftKey(docID));
  } catch (_error) {
    // Nothing to clear.
  }
};

// Saved details win; the draft only fills fields that are still empty.
const withDraft = (normalized, draft) => {
  if (!draft) return { details: normalized, restored: false };
  const merged = { ...normalized };
  let restored = false;
  ["title", "date", "time", "venueName", "address"].forEach((key) => {
    const draftValue = typeof draft[key] === "string" ? draft[key] : "";
    if (!normalized[key] && draftValue.trim()) {
      merged[key] = draftValue;
      restored = true;
    }
  });
  return { details: merged, restored };
};

const firstOpenStep = (details) => {
  const normalized = normalizeEventDetails(details);
  if (!normalized.title) return 1;
  if (!normalized.date) return 2;
  return STEP_COUNT;
};

const describeEventDate = (details) => {
  const normalized = normalizeEventDetails(details);
  if (!normalized.date) return "";
  const [year, month, day] = normalized.date.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `יום ${HEBREW_DAYS[date.getDay()]}, ${day} ${HEBREW_MONTHS[month - 1]} ${year}${normalized.time ? ` · בשעה ${normalized.time}` : ""}`;
};

// A phone keyboard covers the page without resizing it (100dvh stays full height), so
// the window follows the part of the screen that is really visible.
const useVisibleViewport = (rootRef) => {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = rootRef.current;
    if (!viewport || !root) return undefined;
    let fullHeight = viewport.height;
    let fullWidth = viewport.width;
    let wasOpen = false;
    // A shorter screen alone is not a keyboard: it only opens for a field being typed in.
    const isTyping = () => {
      const field = document.activeElement;
      return Boolean(field && field.tagName === "INPUT" && field.type === "text" && !field.readOnly && root.contains(field));
    };
    const update = () => {
      if (viewport.scale > 1.01) return; // Pinch zoom is not a keyboard.
      if (viewport.width !== fullWidth) { fullWidth = viewport.width; fullHeight = 0; }
      fullHeight = Math.max(fullHeight, viewport.height);
      const open = isTyping() && fullHeight - viewport.height > KEYBOARD_MIN_HEIGHT;
      root.style.setProperty("--edm-visible-height", `${viewport.height}px`);
      root.style.setProperty("--edm-visible-top", `${viewport.offsetTop}px`);
      setKeyboardOpen(open);
      if (open && !wasOpen) {
        requestAnimationFrame(() => {
          const field = document.activeElement;
          if (field && field.tagName === "INPUT" && root.contains(field)) field.scrollIntoView({ block: "nearest" });
        });
      }
      wasOpen = open;
    };
    const resetForRotation = () => { fullHeight = 0; };
    let focusFrame = 0;
    const updateAfterFocus = () => {
      cancelAnimationFrame(focusFrame);
      focusFrame = requestAnimationFrame(update);
    };
    update();
    root.addEventListener("focusin", updateAfterFocus);
    root.addEventListener("focusout", updateAfterFocus);
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    window.addEventListener("orientationchange", resetForRotation);
    return () => {
      cancelAnimationFrame(focusFrame);
      root.removeEventListener("focusin", updateAfterFocus);
      root.removeEventListener("focusout", updateAfterFocus);
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", resetForRotation);
    };
  }, [rootRef]);
  return keyboardOpen;
};

// Same picture as Settings → Invitation file: FREE systems show the watermarked image from
// "inspect" (polling while it is prepared); other systems show their attachment.
const useInvitationPreview = (docID, attachmentData, watermarkManaged) => {
  const attachmentUrl = attachmentData?.url || "";
  const attachmentType = attachmentData?.fileType || "";
  const [preview, setPreview] = useState(() => (
    watermarkManaged
      ? { url: "", fileType: "", preparing: true }
      : { url: attachmentUrl, fileType: attachmentType, preparing: false }
  ));

  useEffect(() => {
    if (!watermarkManaged) {
      setPreview({ url: attachmentUrl, fileType: attachmentType, preparing: false });
      return undefined;
    }
    let active = true;
    let timer;
    const poll = async () => {
      let delay = 1500;
      let done = false;
      try {
        const idToken = await auth?.currentUser?.getIdToken();
        if (idToken) {
          const current = await callInvitationWatermark({ idToken, eventId: docID, action: "inspect" });
          if (!active) return;
          setPreview({ url: current.imageUrl || "", fileType: current.fileType || "image/jpeg", preparing: current.preparing === true });
          done = current.preparing !== true;
          // A failed render is retried by the server; keep checking, a little slower.
          if (current.status === "FAILED") delay = 5000;
        }
      } catch (_error) {
        delay = 5000;
      } finally {
        if (active && !done) timer = setTimeout(poll, delay);
      }
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [docID, watermarkManaged, attachmentUrl, attachmentType]);

  return preview;
};

// Same source as Settings; the large step-1 picture asks the image service for a sharper size.
const InvitationPreview = ({ preview, size = 320 }) => (
  <div className="event-details-modal__invitation">
    <FileDisplay
      preparing={preview.preparing}
      source={getSettingsThumbnailUrl(preview.url, preview.fileType, size)}
      localState={preview.fileType || ""}
      setSource={() => {}}
      withButton={false}
      protectFromSaving
    />
  </div>
);

const EventDetailsModal = ({ docID, initialDetails, originalInvitationData, watermarkManaged = false, onSaved, addAlertText }) => {
  const initialTitle = initialDetails?.title;
  const initialDate = initialDetails?.date;
  const initialTime = initialDetails?.time;
  const initialVenueName = initialDetails?.venueName;
  const initialAddress = initialDetails?.address;
  const [initialState] = useState(() => {
    const normalized = normalizeEventDetails(initialDetails);
    const start = withDraft(normalized, readDraft(docID));
    const details = { ...start.details, time: start.details.time || DEFAULT_EVENT_TIME };
    const openStep = firstOpenStep(details);
    return { details, restored: start.restored, step: SHOW_INVITATION_INTRO_STEP && !start.restored && openStep === 1 ? 0 : openStep };
  });
  const [details, setDetails] = useState(initialState.details);
  const [step, setStep] = useState(initialState.step);
  const [restored, setRestored] = useState(initialState.restored);
  const [timeNudge, setTimeNudge] = useState(0);
  const [movedByUser, setMovedByUser] = useState(false);
  const stepBodyRef = useRef(null);
  const rootRef = useRef(null);
  const keyboardOpen = useVisibleViewport(rootRef);
  const preview = useInvitationPreview(docID, originalInvitationData?.attachmentData, watermarkManaged);
  const hasPreview = preview.preparing || Boolean(preview.url);

  // Same eligibility check and editor as Settings → Invitation file.
  const [canEditInvitation, setCanEditInvitation] = useState(false);
  const [openingEditor, setOpeningEditor] = useState(false);
  const editorOpening = useRef(false);
  useEffect(() => {
    let active = true;
    setCanEditInvitation(false);
    if (docID) getInvitationEditorAvailability(docID).then((available) => {
      if (active) setCanEditInvitation(Boolean(available));
    }).catch(() => { /* Unverified eligibility never shows an editing action. */ });
    return () => { active = false; };
  }, [docID]);
  useEffect(() => {
    const restoreAfterBack = (event) => {
      if (event.persisted) { editorOpening.current = false; setOpeningEditor(false); }
    };
    window.addEventListener("pageshow", restoreAfterBack);
    return () => window.removeEventListener("pageshow", restoreAfterBack);
  }, []);
  const editInvitation = async () => {
    if (editorOpening.current || !canEditInvitation) return;
    editorOpening.current = true;
    setOpeningEditor(true);
    let editorUrl;
    try {
      editorUrl = await openInvitationEditor(docID);
      window.location.assign(editorUrl);
      // Navigation is asynchronous: keep the spinner and click guard until this page leaves.
    } catch (error) {
      editorOpening.current = false;
      setOpeningEditor(false);
      if (!editorUrl) setCanEditInvitation(false);
      addAlertText({ text: error.message, mode: "warning" });
    }
  };

  // No invitation file to show: start with the first question.
  useEffect(() => {
    if (step === 0 && !hasPreview) setStep(1);
  }, [step, hasPreview]);
  const initialNavigationPreference = originalInvitationData?.withWazeLink;
  const [navigationPreference, setNavigationPreference] = useState(initialNavigationPreference);
  const hasAddress = Boolean(details.address.trim());
  const withWazeLink = hasAddress && isInvitationNavigationEnabled({
    withWazeLink: navigationPreference,
    locationAddress: details.address,
  });

  useEffect(() => {
    setNavigationPreference(initialNavigationPreference);
  }, [initialNavigationPreference]);

  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    const normalized = normalizeEventDetails({
      title: initialTitle,
      date: initialDate,
      time: initialTime,
      venueName: initialVenueName,
      address: initialAddress,
    });
    const start = withDraft(normalized, readDraft(docID));
    setDetails({ ...start.details, time: start.details.time || DEFAULT_EVENT_TIME });
  }, [
    docID,
    initialTitle,
    initialDate,
    initialTime,
    initialVenueName,
    initialAddress,
  ]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!timeNudge) return undefined;
    const timer = setTimeout(() => setTimeNudge(0), 2400);
    return () => clearTimeout(timer);
  }, [timeNudge]);

  useEffect(() => {
    if (!movedByUser || !stepBodyRef.current) return;
    const firstText = stepBodyRef.current.querySelector('input[type="text"]:not([readonly])');
    if (firstText) firstText.focus();
  }, [step, movedByUser]);

  const updateDetail = (key, value) => {
    setValidationError("");
    setDetails((current) => {
      const next = { ...current, [key]: value };
      writeDraft(docID, next);
      return next;
    });
  };

  const showProblem = (message) => {
    setValidationError(message);
    addAlertText({ text: message, mode: "warning" });
  };

  const goToStep = (nextStep) => {
    setValidationError("");
    setRestored(false);
    setMovedByUser(true);
    setStep(nextStep);
  };

  const goNext = () => {
    if (step === 0) {
      goToStep(1);
      return;
    }
    const normalized = normalizeEventDetails(details);
    if (step === 1 && !normalized.title) {
      showProblem("נא לכתוב שם לאירוע.");
      return;
    }
    if (step === 2) {
      if (!normalized.date) {
        showProblem("נא לבחור תאריך לאירוע.");
        return;
      }
      if (!isEventDateTimeInFuture(normalized)) {
        showProblem("יש לבחור תאריך ושעה עתידיים לאירוע.");
        return;
      }
    }
    goToStep(step + 1);
  };

  const saveDetails = async (event) => {
    event.preventDefault();
    if (step < STEP_COUNT) {
      goNext();
      return;
    }
    const normalized = normalizeEventDetails(details);
    if (getMissingEventDetailKeys(normalized).length > 0) {
      const message = "נא להשלים את כל פרטי האירוע לפני שממשיכים.";
      setValidationError(message);
      addAlertText({ text: message, mode: "warning" });
      setStep(firstOpenStep(normalized));
      return;
    }
    if (!isEventDateTimeInFuture(normalized)) {
      const message = "יש לבחור תאריך ושעה עתידיים לאירוע.";
      setValidationError(message);
      addAlertText({ text: message, mode: "warning" });
      return;
    }

    setSaving(true);
    try {
      await addDocumentData({
        docID,
        payload: {
          title: normalized.title,
          eventDetails: {
            date: normalized.date,
            time: normalized.time,
            venueName: normalized.venueName,
            address: normalized.address,
          },
          "invitationData.eventOwners": normalized.title,
          "invitationData.date": formatEventDateForDisplay(normalized.date),
          "invitationData.day": "בשעה " + normalized.time,
          "invitationData.locationName": normalized.venueName,
          "invitationData.locationAddress": normalized.address,
          "invitationData.withWazeLink": withWazeLink,
        },
      });
      clearDraft(docID);
      addAlertText({ text: "פרטי האירוע נשמרו בהצלחה.", mode: "success" });
      onSaved({ ...normalized, withWazeLink });
    } catch (error) {
      addAlertText({ text: "לא הצלחנו לשמור את פרטי האירוע. נסו שוב.", mode: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const whenText = describeEventDate(details);

  return (
    <div className="event-details-modal" dir="rtl" role="presentation" ref={rootRef}>
      <section
        className={`event-details-modal__dialog ${step > 0 && hasPreview ? "has-aside" : ""} ${step === 0 ? "is-intro" : ""} ${step === 1 ? "is-first-question" : ""} ${keyboardOpen ? "is-keyboard-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-details-modal-title"
      >
        <header className="event-details-modal__header">
          <span className="event-details-modal__eyebrow">{step === 0 ? "ההזמנה שלכם" : "פרטי האירוע"}</span>
          {step > 0 && (
            <>
              <div className="event-details-modal__progress" aria-hidden="true">
                {Array.from({ length: STEP_COUNT }, (_, index) => (
                  <i key={index} className={index < step ? "is-done" : ""} />
                ))}
              </div>
              <span className="event-details-modal__step-count">שאלה {step} מתוך {STEP_COUNT}</span>
            </>
          )}
        </header>

        <form className="event-details-modal__form" onSubmit={saveDetails} noValidate>
          <div className="event-details-modal__body" ref={stepBodyRef}>
            {step > 0 && hasPreview && (
              <aside className="event-details-modal__aside" aria-label="ההזמנה שלכם">
                <InvitationPreview preview={preview} />
                {step === 1 ? (
                  <span className="event-details-modal__edit-note">
                    <span>ניתן לערוך ולתקן את ההזמנה</span>{" "}
                    <span>בכל שלב בלשונית "הגדרות"</span>{" "}
                    <span>במערכת שלכם</span>
                  </span>
                ) : <span>ההזמנה שלכם</span>}
                {canEditInvitation && (step !== 1 || SHOW_EDIT_ON_FIRST_QUESTION) && (
                  <button type="button" className="event-details-modal__edit event-details-modal__edit--outline" onClick={editInvitation} disabled={openingEditor} aria-busy={openingEditor}>
                    {openingEditor && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                    עריכה
                  </button>
                )}
              </aside>
            )}
            <div className="event-details-modal__main">
            {restored && (
              <p className="event-details-modal__restored">שמרנו את מה שמילאתם, ממשיכים מאיפה שהפסקתם.</p>
            )}

            {step === 0 && (
              <div className="event-details-modal__intro">
                <h2 id="event-details-modal-title">זו ההזמנה שתישלח לאורחים</h2>
                <InvitationPreview preview={preview} size={1024} />
                {canEditInvitation && (
                  <>
                    <p className="event-details-modal__edit-helper">אפשר לערוך את ההזמנה עכשיו וגם אחר כך במערכת</p>
                    <button type="button" className="event-details-modal__edit" onClick={editInvitation} disabled={openingEditor} aria-busy={openingEditor}>
                      {openingEditor && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
                      עריכת ההזמנה
                    </button>
                  </>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="event-details-modal__question">
                <div className="event-details-modal__welcome">
                  <img src={WELCOME_ROBOT} alt="" />
                  <span>כמה פרטים קטנים ומתחילים לחגוג</span>
                </div>
                <h2 id="event-details-modal-title">כותרת האירוע שלכם?</h2>
                <input
                  type="text"
                  value={details.title}
                  maxLength={120}
                  autoComplete="off"
                  enterKeyHint="next"
                  aria-label="שם האירוע"
                  placeholder="לדוגמא: החתונה של מיכל ואיתן"
                  onChange={(event) => updateDetail("title", event.target.value)}
                  required
                />
                <div className="event-details-modal__title-starts" aria-label="התחלות מוכנות">
                  {TITLE_STARTS.map((start) => (
                    <button
                      key={start}
                      type="button"
                      onClick={(event) => {
                        updateDetail("title", start);
                        const input = event.currentTarget.closest(".event-details-modal__question").querySelector("input");
                        input.focus();
                        setTimeout(() => input.setSelectionRange(start.length, start.length));
                      }}
                    >
                      {start.trim()}…
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="event-details-modal__question">
                <h2 id="event-details-modal-title">מתי האירוע?</h2>
                <div className="event-details-modal__date-field">
                  <span>תאריך</span>
                  <EventDatePicker
                    value={details.date}
                    onChange={(value) => {
                      updateDetail("date", value);
                      if (value) setTimeNudge(Date.now());
                    }}
                    id="completion-event-date"
                    placeholderText="בחירת תאריך"
                    ariaLabel="תאריך האירוע"
                  />
                </div>
                <div
                  key={timeNudge}
                  className={`event-details-modal__time-field ${timeNudge ? "is-nudged" : ""}`}
                >
                  <span>שעה{timeNudge ? " · אפשר לשנות את השעה" : ""}</span>
                  <EventTimePicker
                    value={details.time}
                    onChange={(value) => updateDetail("time", value)}
                    idPrefix="completion-event-time"
                  />
                </div>
                {whenText && <p className="event-details-modal__when">{whenText}</p>}
              </div>
            )}

            {step === 3 && (
              <div className="event-details-modal__question">
                <h2 id="event-details-modal-title">איפה האירוע?</h2>
                <label>
                  <span>שם המקום</span>
                  <input
                    type="text"
                    value={details.venueName}
                    maxLength={160}
                    enterKeyHint="next"
                    placeholder="לדוגמה: גן האירועים הטירה"
                    onChange={(event) => updateDetail("venueName", event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      event.currentTarget.form?.querySelector('[data-field="address"]')?.focus();
                    }}
                    required
                  />
                </label>

                <label>
                  <span>כתובת</span>
                  <input
                    type="text"
                    data-field="address"
                    value={details.address}
                    maxLength={300}
                    enterKeyHint="done"
                    placeholder="עיר, רחוב ומספר"
                    onChange={(event) => updateDetail("address", event.target.value)}
                    required
                  />
                </label>

                <div className="event-details-modal__navigation">
                  <Waze
                    withWazeLink={withWazeLink}
                    setWithWazeLink={setNavigationPreference}
                    disabled={!hasAddress || saving}
                  />
                </div>
              </div>
            )}

            {validationError && (
              <p className="event-details-modal__validation-error" role="alert">{validationError}</p>
            )}
            </div>
          </div>

          <footer className={`event-details-modal__footer ${step === 0 ? "is-centered" : ""}`}>
            {step > 1 || (step === 1 && hasPreview && SHOW_INVITATION_INTRO_STEP) ? (
              <button type="button" className="event-details-modal__back" onClick={() => goToStep(step - 1)} disabled={saving}>
                חזרה
              </button>
            ) : <span />}
            <button type="submit" className="event-details-modal__submit" disabled={saving}>
              {step === 0 ? "ממשיכים לפרטי האירוע" : step < STEP_COUNT ? "הבא" : saving ? "שומר..." : "שמירת פרטי האירוע"}
            </button>
          </footer>
          <p className="event-details-modal__settings-note">ניתן לשנות שוב בתפריט "הגדרות"</p>
        </form>
      </section>
    </div>
  );
};

export default EventDetailsModal;
